import { useCallback, useEffect, useMemo } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { WarningMessages } from 'constants/Warning.constants';
import { useFieldArray, useForm } from 'react-hook-form';

import {
  selectActiveSmsTemplates,
  selectSmsById,
} from '@polyai/stream/store/reducers/sms/selectors';
import { InfoIcon, SmsIcon } from '@polyai/ui/components/atoms/Icons';
import Tooltip from '@polyai/ui/components/atoms/Tooltip';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';

import CreateFunctionButton from 'components/molecules/LexicalEditor/components/CreateFunctionButton';
import {
  FUNCTION_PREFIX,
  TWILIO_SMS_PREFIX,
} from 'components/molecules/LexicalEditor/features/action/constants';
import type {
  ActionTagContentProps,
  ActionTypeInput,
  ActionTypeOutput,
} from 'components/molecules/LexicalEditor/features/action/types';
import { switchTagReference } from 'components/molecules/LexicalEditor/features/action/utils';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import NewSmsTemplatesModal from 'components/organisms/SmsTemplatesModal';
import { useAppSelector } from 'hooks/reduxHooks';
import { useBoolean } from 'hooks/useBoolean';
import useSmsCredentials from 'hooks/useSmsCredentials';
import type { ConnectionFormData } from 'screens/SmsTemplates/components/ConnectionModalContent';
import ConnectionModalContent from 'screens/SmsTemplates/components/ConnectionModalContent';
import { SMS_TEMPLATES } from 'screens/SmsTemplates/SmsTemplates.uiCopy';
import { useViewMode } from 'screens/withPermissions/useViewMode';
import { convertToFunctionButton } from './TwilioSms.copy';
import * as Styled from './TwilioSms.styled';
import { generateAdvancedSMSCode } from './utils';

const DEFAULT_FORM_VALUES = {
  account_sid: '',
  auth_token: '',
  numbers: [{ value: '' }],
};

const ConvertToFunctionTrigger = (
  <Styled.ConvertToFunctionTrigger>
    <span>{convertToFunctionButton.triggerText}</span>
    <Tooltip content={convertToFunctionButton.tooltip} placement="top">
      <InfoIcon />
    </Tooltip>
  </Styled.ConvertToFunctionTrigger>
);

const TwilioSmsTagContent = ({
  id,
  nodeKey,
  name,
  closePopup,
  remove,
}: ActionTagContentProps) => {
  const [editor] = useLexicalComposerContext();

  const { value: isEditModalOpen, setTrue: openEditModal } = useBoolean(false);
  const twilioSmsModal = useBoolean();
  const smsTemplateModal = useBoolean();
  const connectionFormMethods = useForm<ConnectionFormData>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onBlur',
  });

  const { control, watch, reset, formState } = connectionFormMethods;
  const { isValid } = formState;

  const connectionFormFieldMethods = useFieldArray({
    control,
    name: 'numbers',
  });

  const connectionData = watch();
  const {
    smsCredentials,
    isLoadingSmsCredentials,
    upsertSmsCredentials,
    isUpsertingSmsCredentials,
    smsCredentialsError,
  } = useSmsCredentials();

  const existingSmsCredentials = useMemo(
    () => ({
      account_sid: smsCredentials?.account_sid,
      auth_token: smsCredentials?.auth_token,
      numbers: smsCredentials?.numbers?.map(num => ({ value: num })),
    }),
    [smsCredentials],
  );

  useEffect(() => {
    if (existingSmsCredentials) {
      reset(existingSmsCredentials);
    }
  }, [existingSmsCredentials, reset]);

  const templates = useAppSelector(selectActiveSmsTemplates);
  const smsTemplate = templates?.find(template => template.id === id);

  const { isEditable } = useViewMode();

  const hasNoCredentials = useMemo(
    () => !Object.keys(smsCredentials ?? {}).length,
    [smsCredentials],
  );

  const handleConnectionSave = useCallback(
    (isEdit: boolean) => {
      const numbersForReq = connectionData.numbers
        .map(num => num.value)
        .filter(num => !!num);

      upsertSmsCredentials({
        request: {
          ...connectionData,
          numbers: numbersForReq,
        },
        isEdit,
      });
    },
    [connectionData, upsertSmsCredentials],
  );

  const editSmsTemplateTags =
    (closeMenu?: () => void) =>
    (newSmsName: string, existingSmsName?: string) => {
      if (newSmsName !== existingSmsName && existingSmsName) {
        closeMenu?.();
      }
    };

  const onGenerateAdvancedSMS = (_: string, id: string) => {
    switchTagReference(editor, nodeKey, FUNCTION_PREFIX, id);
    closePopup?.();
  };

  const templateExists = !!smsTemplate;

  if (smsCredentialsError) {
    throw smsCredentialsError;
  }

  return (
    <>
      {templateExists && !hasNoCredentials && (
        <>
          <PopupMenuItem onClick={openEditModal}>
            {isEditable ? 'Edit' : 'View'}
          </PopupMenuItem>
          <NewSmsTemplatesModal
            isEdit={isEditable}
            isRequesting={isUpsertingSmsCredentials}
            open={isEditModalOpen}
            readonly={!isEditable}
            setIsOpen={openEditModal}
            template={smsTemplate}
            onClose={closePopup}
            onUpsertSuccess={editSmsTemplateTags(closePopup)}
          />
        </>
      )}

      {hasNoCredentials && !templateExists && (
        <PopupMenuItem
          isDisabled={isLoadingSmsCredentials}
          onClick={twilioSmsModal.setTrue}
        >
          Connect Twilio account
        </PopupMenuItem>
      )}

      {!hasNoCredentials && !templateExists && (
        <PopupMenuItem
          isDisabled={isLoadingSmsCredentials}
          onClick={smsTemplateModal.setTrue}
        >
          Create
        </PopupMenuItem>
      )}

      {isEditable && !hasNoCredentials && templateExists && (
        <CreateFunctionButton
          code={generateAdvancedSMSCode(name)}
          description="Send SMS"
          subtitle={convertToFunctionButton.subtitle}
          title={convertToFunctionButton.title}
          trigger={ConvertToFunctionTrigger}
          onCreate={onGenerateAdvancedSMS}
        />
      )}

      {(isEditable || !templateExists) && (
        <PopupMenuItem isDelete onClick={remove}>
          Remove
        </PopupMenuItem>
      )}

      {twilioSmsModal.value && (
        <ConfirmationModal
          buttonText={SMS_TEMPLATES.modal.twilio.button.confirm}
          cancelText={SMS_TEMPLATES.modal.twilio.button.cancel}
          confirmDisabled={!isValid}
          confirmRequesting={isUpsertingSmsCredentials}
          content={
            <ConnectionModalContent
              connection={connectionData}
              fieldMethods={connectionFormFieldMethods}
              formMethods={connectionFormMethods}
            />
          }
          open={twilioSmsModal.value}
          size="md"
          title={SMS_TEMPLATES.modal.twilio.title}
          onClose={() => {
            reset(DEFAULT_FORM_VALUES);
            twilioSmsModal.setFalse();
          }}
          onConfirm={() => handleConnectionSave(true)}
        />
      )}

      {!templateExists && !hasNoCredentials && (
        <NewSmsTemplatesModal
          open={smsTemplateModal.value}
          setIsOpen={smsTemplateModal.setTrue}
          // @ts-ignore
          template={{
            name,
          }}
          onClose={smsTemplateModal.setFalse}
        />
      )}
    </>
  );
};

export const useTwilioSmsActionType = ({
  id,
}: ActionTypeInput): ActionTypeOutput => {
  const { isSmsEnabled } = useActionsContext();
  const smsTemplate = useAppSelector(state => selectSmsById(state, id));

  const warning = !smsTemplate?.active
    ? WarningMessages.SMS_NOT_EXIST
    : undefined;

  return {
    Content: TwilioSmsTagContent,
    Icon: SmsIcon,
    name: smsTemplate?.name ?? id,
    warning: warning,
    prefix: TWILIO_SMS_PREFIX,
    isEnabled: isSmsEnabled,
    isLoading: false,
  };
};
